import './index.css'
import React, { useState } from 'react';
import {Button, Divider, Dropdown, MenuProps, message, Modal, Space, Tag, Tooltip} from 'antd';
import {getId} from '../../helpers/file';
import QRCode from "react-qr-code";
import {CopyOutlined, QuestionCircleOutlined, InfoCircleOutlined, EditOutlined} from "@ant-design/icons";
import {appendQuery, isRandomPath} from "../../helpers/url";
import {convertImageToBase64, isImg} from "../../helpers/img";
import {recordImageError} from "../../helpers/imageError";
import {
  copyToClipboard,
  getStoredWatermarkImageUrl,
  getStoredWatermarkText,
  saveWatermarkImageUrl,
  saveWatermarkText,
  urlSafeBase64Encode
} from '../../helpers/clipboard';
import Img from "../Img";
import {getDate, getTime} from '../../utils/dateUtils';
import {getFileType, getName, isZip} from '../../utils/fileTypeUtils';
import {getPreview} from '../../utils/fileIconMap';
import {useIsMobile} from '../../hooks/useIsMobile';
import {getIllicitReason} from '../../utils/illicitUtils';
import {getStorageTypeDescription, getExpireTimeDisplay} from '../../utils/storageTypeUtils';
import ShortUrlModal from '../ShortUrlModal';
import ExpireTimeModal from '../ExpireTimeModal';
import EncryptedLinkModal from '../EncryptedLinkModal';
import { useTranslation } from '../../hooks/useTranslation';
import { isEnglishSite } from '../../config/site';


/**
 *
 * @author dongdong.fdd
 * @Company AutoNavi Co.,Ltd.
 * @date 2023/7/29 15:30
 */
// @ts-ignore
function UploadItem({dir, id, fileName, time, url, rawUrl, contentType, size, illicit, illicitDetails, storageType, expireTime, onDelete, onRefresh, userInfo, index, selected, onSelect, selectedIds}) {
  const { t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();
  const [shortUrlModalOpen, setShortUrlModalOpen] = useState(false);
  const [expireTimeModalOpen, setExpireTimeModalOpen] = useState(false);
  const [encryptedLinkModalOpen, setEncryptedLinkModalOpen] = useState(false);
  const isMobile = useIsMobile();
  
  // 判断是否是VIP用户
  const isVip = userInfo?.detail?.isActiveVip || userInfo?.isActiveVip || false;

  // 获取违规原因
  const illicitReason = getIllicitReason(illicit, illicitDetails);

  const handleCopyOriginUrl = (content = '') => {
    const key = 'origin_token';
    if (localStorage.getItem(key) && /^\d\d\d$/.test(localStorage.getItem(key) || '')) {
              copyToClipboard(content, t('uploadItem.originalLinkCopied'));
    } else {
      // 没有口令
      const value = window.prompt(t('uploadItem.passwordPrompt'));
      if (value && /^\d\d\d$/.test(value)) {
        localStorage.setItem(key, value);
        copyToClipboard(content, t('uploadItem.originalLinkCopied'));
      } else {
        alert(t('uploadItem.passwordError'))
      }
    }
  }

  const handleImgLoadError = (e: any) => {
    console.info('e is', e);
    const img = e.target as HTMLImageElement;
    const currentSrc = img.src;
    
    // 检查当前URL是否包含参数
    const hasParams = currentSrc.includes('?');
    
    // 如果包含参数且还没有尝试过无参数版本，则尝试去掉参数重新加载
    if (hasParams && !img.dataset.retried) {
      const baseUrl = currentSrc.split('?')[0];
      console.info('图片加载失败，尝试去掉参数重新加载:', baseUrl);
      
      // 标记已经重试过，防止无限循环
      img.dataset.retried = 'true';
      img.src = baseUrl;
      return;
    }
    
    // 记录图片加载失败错误
    const imageUrl = currentSrc || 'unknown';
    const errorMessage = t('uploadItem.imageLoadFailed', { url: imageUrl });
    recordImageError(imageUrl, errorMessage);
    
    // 根据原始URL判断使用哪个兜底图片
    const fallbackImg = currentSrc.includes('autoupload/fr') 
      ? 'https://edgeone.51shazhu.com/autoupload/fdd/default/guoqi.svg'
      : 'https://z.wiki/autoupload/20230831/l32P.%E5%9B%BE%E7%89%87%E5%8A%A0%E8%BD%BD%E5%A4%B1%E8%B4%A5_%282%29.svg';
    
    // 设置为失败图片，并清空重试标记
    img.onerror = null;
    delete img.dataset.retried;
    img.src = fallbackImg;
  }

  // 处理图片链接的公共函数
  const handleImageProcess = (apiUrl: string, title: string) => {
    messageApi.info({
      content: t('uploadItem.processing'),
      key: 'imageProcessLoading'
    })
    fetch(apiUrl)
      .then(res => res.json())
      .then(data => {
        if (data.url) {
          Modal.info({
            title: t('uploadItem.processResult', { title }),
            content: (
              <div>
                <a href={data.url} target="_blank" rel="noopener noreferrer">{data.url}</a>
                <img 
                  src={`https://playground.z.wiki/qrcode/generateQRCode?data=${encodeURIComponent(data.url)}`}
                  alt="QR Code"
                  style={{ 
                    display: 'block',
                    margin: '0 auto'
                  }}
                />
                <br />
                <br />
              </div>
            ),
            footer: (
              <div style={{float: 'right'}}>
                <Space>
                  <Button
                    type="primary"
                    onClick={() => {
                      copyToClipboard(data.url, t('uploadItem.processedLinkCopied'));
                      Modal.destroyAll();
                    }}
                  >
                    {t('uploadItem.copyLink')}
                  </Button>
                  <Button onClick={() => Modal.destroyAll()}>
                    {t('common.close')}
                  </Button>
                </Space>
              </div>
            )
          });
        } else {
          messageApi.open({
            type: 'error',
            content: t('uploadItem.processFailed'),
          });
        }
      })
      .catch(err => {
        messageApi.open({
          type: 'error',
          content: t('uploadItem.requestFailed', { message: err.message }),
        });
      });
  };

  const doDelete = (hard: boolean, id: string) => {
    console.info('onClick')
    messageApi.open({
      type: 'info',
      content: t('uploadItem.deleting'),
    });
    fetch(`/img/delete?hard=${hard}&id=${id}&uid=${getId()}`, {
      method: 'DELETE'
    }).then(res => res.json()).then((flag) => {
      if (flag) {
        messageApi.open({
          type: 'success',
          content: t('uploadItem.deleteSuccess')
        })
        onDelete && onDelete();
      } else {
        messageApi.open({
          type: 'error',
          content: t('uploadItem.deleteFailed')
        })
      }
    })
  }

  const moreItems: MenuProps['items'] = [
    // {
    //   key: 'soft-delete',
    //   label: '删除到回收站',
    //   onClick() {
    //     Modal.confirm({
    //       title: '删除到回收站',
    //       content: '确定要删除这个文件吗？文件将移动到回收站，7 天后自动清理。',
    //       okText: '确定',
    //       cancelText: '取消',
    //       onOk() {
    //         doDelete(false, id);
    //       },
    //     });
    //   }
    // },
    {
      key: 'hard-delete',
      label: t('uploadItem.hardDelete'),
      danger: true,
      onClick() {
        Modal.confirm({
          title: t('uploadItem.hardDelete'),
          content: t('uploadItem.hardDeleteConfirm'),
          okText: t('uploadItem.hardDeleteOk'),
          cancelText: t('common.cancel'),
          okType: 'danger',
          onOk() {
            doDelete(true, id);
          },
        });
      }
    }
  ];

  if (userInfo.config?.imgViewer) {
    moreItems.push({
      key: 2,
      label: t('uploadItem.customPreview'),
      onClick(row) {
        console.info('onClick ', row, rawUrl);
        copyToClipboard(`${userInfo.config?.imgViewer}?url=${rawUrl || url}`, t('uploadItem.customPreviewLinkCopied'));
      }
    })
  }

  // 获取链接下拉菜单选项（仅对图片显示）
  const getLinkItems: MenuProps['items'] = [
    {
      key: 'original',
      label: t('uploadItem.originalLink'),
      onClick() {
        copyToClipboard(url, t('uploadItem.originalLinkCopied'));
      }
    },
    {
      key: 'compressed',
      label: t('uploadItem.compressedLink'),
      onClick() {
        // 使用 /webp 参数进行无损压缩
        const [pathname, search] = url.split('?');
        const compressedUrl = search ? `${pathname}/webp?${search}` : `${pathname}/webp`;
        copyToClipboard(compressedUrl, t('uploadItem.compressedLinkCopied'));
      }
    },
    {
      key: 'overseas',
      label: t('uploadItem.overseasLink'),
      onClick() {
        // 使用海外CDN加速
        const urlObj = new URL(url);
        const overseasUrl = `https://xland.eu.org${urlObj.pathname}${urlObj.search}`;
        copyToClipboard(overseasUrl, t('uploadItem.overseasLinkCopied'));
      }
    },
    {
      type: 'divider'
    },
    {
      key: 'encrypted',
      label: t('uploadItem.encryptedLink'),
      onClick() {
        setEncryptedLinkModalOpen(true);
      }
    }
  ];

  // 照片处理下拉菜单选项
  const photoProcessItems: MenuProps['items'] = [
    {
      key: 'base64',
      label: t('uploadItem.base64Format'),
      onClick() {
        let loadingMessage: (() => void) | null = null;
        
        convertImageToBase64(
          url,
          (base64String) => {
            // 成功回调
            copyToClipboard(base64String, t('uploadItem.base64Copied'));
          },
          (errorMessage) => {
            // 错误回调
            message.error(errorMessage);
          },
          (loading) => {
            // 加载状态回调
            if (loading) {
              loadingMessage = message.loading(t('uploadItem.convertingBase64'), 0);
            } else {
              loadingMessage?.();
            }
          }
        );
      }
    },
    {
      key: 'webp',
      label: t('uploadItem.webpFormat'),
      onClick() {
        // 直接处理字符串，在查询参数前添加 /webp
        const [pathname, search] = url.split('?');
        const webpUrl = search ? `${pathname}/webp?${search}` : `${pathname}/webp`;
        copyToClipboard(webpUrl, t('uploadItem.webpCopied'));
      }
    },
    {
      type: 'divider'
    },
    {
      key: 'thumbnail_100',
      label: t('uploadItem.thumbnail100'),
      onClick() {
        const separator = url.includes('?') ? '&' : '?';
        const thumbnailUrl = `${url}${separator}imageMogr2/thumbnail/100x`;
        copyToClipboard(thumbnailUrl, t('uploadItem.thumbnail100Copied'));
      }
    },
    {
      key: 'thumbnail_200',
      label: t('uploadItem.thumbnail200'),
      onClick() {
        const separator = url.includes('?') ? '&' : '?';
        const thumbnailUrl = `${url}${separator}imageMogr2/thumbnail/200x`;
        copyToClipboard(thumbnailUrl, t('uploadItem.thumbnail200Copied'));
      }
    },
    {
      key: 'thumbnail_300',
      label: t('uploadItem.thumbnail300'),
      onClick() {
        const separator = url.includes('?') ? '&' : '?';
        const thumbnailUrl = `${url}${separator}imageMogr2/thumbnail/300x`;
        copyToClipboard(thumbnailUrl, t('uploadItem.thumbnail300Copied'));
      }
    },
    {
      key: 'thumbnail_600',
      label: t('uploadItem.thumbnail600'),
      onClick() {
        const separator = url.includes('?') ? '&' : '?';
        const thumbnailUrl = `${url}${separator}imageMogr2/thumbnail/600x`;
        copyToClipboard(thumbnailUrl, t('uploadItem.thumbnail600Copied'));
      }
    },
    {
      type: 'divider'
    },
    {
      key: 'watermark_default',
      label: t('uploadItem.defaultWatermark'),
      onClick() {
        const storedText = getStoredWatermarkText();
        const customText = window.prompt(t('uploadItem.watermarkTextPrompt'), storedText);
        if (customText) {
          if (customText.length > 10) {
            message.error(t('uploadItem.watermarkTextTooLong'));
            return;
          }
          saveWatermarkText(customText);
          const separator = url.includes('?') ? '&' : '?';
          const text = urlSafeBase64Encode(customText);
          const watermarkUrl = `${url}${separator}watermark/2/text/${text}/fontsize/20/fill/IzAwMDAwMA/fontcolor/ffffff/t/50`;
          copyToClipboard(watermarkUrl, t('uploadItem.defaultWatermarkCopied'));
        }
      }
    },
    {
      key: 'watermark_tile',
      label: t('uploadItem.tileWatermark'),
      onClick() {
        const storedText = getStoredWatermarkText();
        const customText = window.prompt(t('uploadItem.watermarkTextPrompt'), storedText);
        if (customText) {
          if (customText.length > 10) {
            message.error(t('uploadItem.watermarkTextTooLong'));
            return;
          }
          saveWatermarkText(customText);
          const separator = url.includes('?') ? '&' : '?';
          const text = urlSafeBase64Encode(customText);
          const watermarkUrl = `${url}${separator}watermark/2/text/${text}/fontsize/20/fill/IzAwMDAwMA/fontcolor/ffffff/dissolve/10/gravity/center/batch/1/degree/30`;
          copyToClipboard(watermarkUrl, t('uploadItem.tileWatermarkCopied'));
        }
      }
    },
    {
      key: 'watermark_southeast',
      label: '右下角水印',
      onClick() {
        const storedText = getStoredWatermarkText();
        const customText = window.prompt(t('uploadItem.watermarkTextPrompt'), storedText);
        if (customText) {
          if (customText.length > 10) {
            message.error(t('uploadItem.watermarkTextTooLong'));
            return;
          }
          saveWatermarkText(customText);
          const separator = url.includes('?') ? '&' : '?';
          const text = urlSafeBase64Encode(customText);
          const watermarkUrl = `${url}${separator}watermark/2/text/${text}/fontsize/16/fill/IzAwMDAwMA/fontcolor/ffffff/t/50/gravity/southeast`;
          copyToClipboard(watermarkUrl, t('body.southeastWatermarkCopied'));
        }
      }
    },
    {
      key: 'image_watermark',
      label: t('uploadItem.imageWatermark'),
      onClick() {
        const storedImageUrl = getStoredWatermarkImageUrl();
        const watermarkImageUrl = window.prompt(t('uploadItem.watermarkImagePrompt'), storedImageUrl || 'https://z.wiki/autoupload/20230831/l32P.%E5%9B%BE%E7%89%87%E5%8A%A0%E8%BD%BD%E5%A4%B1%E8%B4%A5_%282%29.svg');
        if (watermarkImageUrl) {
          // 验证URL格式
          if (!watermarkImageUrl.startsWith('http://') && !watermarkImageUrl.startsWith('https://')) {
            message.error(t('uploadItem.watermarkImageInvalid'));
            return;
          }

          saveWatermarkImageUrl(watermarkImageUrl);
          
          // 从水印图片URL中截取路径
          let imageKey = '';
          try {
            const urlObj = new URL(watermarkImageUrl);
            // 提取路径部分，去掉域名
            imageKey = urlObj.pathname;
            // 如果路径以/开头，去掉开头的/
            if (imageKey.startsWith('/')) {
              imageKey = imageKey.substring(1);
            }
          } catch (e) {
            message.error(t('uploadItem.watermarkImageUrlError'));
            return;
          }
          
          const separator = url.includes('?') ? '&' : '?';
          const encodedImageKey = urlSafeBase64Encode(imageKey);
          // 图片水印参数：1表示图片水印，image_key/编码后的路径，gravity位置，ws宽度，ht高度
          const watermarkUrl = `${url}${separator}watermark/1/image_key/${encodedImageKey}/gravity/southeast/ws/100/ht/50`;
          copyToClipboard(watermarkUrl, t('body.imageWatermarkCopied'));
        }
      }
    }
  ];

  return (
    <div
      className="UploadItem"
      style={{
        width: isMobile ? '100%' : '324px',
        marginBottom: isMobile ? '12px' : '28px',
        backgroundColor: "#FFFFFF",
        borderRadius: "6px",
        overflow: 'hidden',
        position: 'relative',
        ...(isMobile ? { border: '1px solid #f0f0f0' } : {})
      }}
    >
      {/* Selection Checkbox */}
      <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 2, display: 'flex', alignItems: 'center' }}>
        <input
          type="checkbox"
          checked={!!selected}
          onChange={e => onSelect && onSelect(id, e.target.checked)}
          className="upload-item-checkbox"
          style={{
            width: 18,
            height: 18,
          }}
          title={t('uploadItem.selectThis')}
        />
        {/* 序号展示 */}
        {selected && selectedIds && selectedIds.includes(id) && (
          <span style={{ color: '#2095f2', fontWeight: 600, marginLeft: 4, fontSize: 13 }}>
            {selectedIds.indexOf(id) + 1}
          </span>
        )}
      </div>
      {contextHolder}
      
      {/* 桌面版显示大图 */}
      {!isMobile && (
        <img
          className="img"
          src={illicit === 1 ? 'https://a.tuchuangyun.top/autoupload/fdd/default/%E8%BF%9D%E7%A6%81.svg' : (isImg(url) ? (isRandomPath(url) ? appendQuery(url, "imageMogr2/thumbnail/700x"): appendQuery(url, `id=` + id)) : getPreview(url))}
          onError={handleImgLoadError}
          loading={'lazy'}
          onClick={() => onSelect && onSelect(id, !selected)}
          alt={decodeURIComponent(getName(decodeURIComponent(fileName)))}
          style={{
            height: '188px',
            width: '100%',
            backgroundRepeat: 'no-repeat',
            objectFit: "fill",
            backgroundSize: "unset",
            backgroundPosition: "center center",
            padding: "15px",
            boxSizing: "border-box",
            cursor: 'pointer',
          }}
        />
      )}
      <div
        className="summary"
        style={{
          paddingTop: '12px',
          paddingLeft: '14px',
          paddingRight: '14px',
          paddingBottom: '20px',
        }}
      >
        {isMobile ? (
          // 移动端布局：图片在H3前面
          <div className="info" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <Img
              className="wap-thumbnail"
              src={illicit === 1 ? 'https://a.tuchuangyun.top/autoupload/fdd/default/%E8%BF%9D%E7%A6%81.svg' : (isImg(url) ? (isRandomPath(url) ? appendQuery(url, "imageMogr2/thumbnail/100x") : appendQuery(url, `id=` + id)) : getPreview(url))}
              onError={handleImgLoadError}
              showPreview={isImg(url) && illicit !== 1}
              loading={'lazy'}
              previewSize='original'
              onClick={() => onSelect && onSelect(id, !selected)}
              alt={decodeURIComponent(getName(decodeURIComponent(fileName)))}
              style={{
                width: '48px',
                height: '48px',
                objectFit: 'cover',
                borderRadius: '4px',
                flexShrink: 0,
                cursor: 'pointer',
              }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3
                className="file-name"
                style={{
                  direction: "rtl",
                  paddingBottom: '4px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  margin: 0,
                  "height": "20px",
                  "fontFamily": "PingFangSC",
                  "fontWeight": 400,
                  "fontSize": "14px",
                  "color": illicitReason ? "#ff4d4f" : "#21242E",
                  "letterSpacing": "0",
                  textAlign: "left",
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                title={illicitReason || decodeURIComponent(getName(decodeURIComponent(fileName)))}
              >
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {illicitReason || decodeURIComponent(getName(decodeURIComponent(fileName)))}
                </span>
                {illicitReason && (
                  <Tooltip title={t('error.contactOnError')}>
                    <QuestionCircleOutlined style={{ fontSize: '12px', color: '#ff4d4f', cursor: 'help', flexShrink: 0 }} />
                  </Tooltip>
                )}
              </h3>
              <div
                className="text-info"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px',
                  "fontFamily": "PingFangSC",
                  "fontWeight": 400,
                  "fontSize": "12px",
                  "color": "#757D8B",
                  "letterSpacing": "0"
                }}
              >
                <div style={{"display": "flex", "justifyContent": "space-between"}}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {storageType ? (
                      <>
                        <span style={{ color: storageType === 'TEMPORARY' ? '#ff4d4f' : 'inherit' }}>
                          {getExpireTimeDisplay(storageType, expireTime)}
                        </span>
                        {storageType === 'TEMPORARY' && (
                          <Tooltip title={t('common.temporaryStorageHint')}>
                            <InfoCircleOutlined style={{ fontSize: '12px', color: '#ff4d4f', cursor: 'help' }} />
                          </Tooltip>
                        )}
                        {storageType === 'LONG_TERM' && (
                          <Tooltip title={t('common.clickToSetExpireTime')}>
                            <EditOutlined 
                              style={{ fontSize: '12px', color: '#1890ff', cursor: 'pointer' }} 
                              onClick={() => setExpireTimeModalOpen(true)}
                            />
                          </Tooltip>
                        )}
                      </>
                    ) : (
                      `${t('common.format')}：${getFileType(contentType, fileName)}`
                    )}
                  </span>
                  <span>{t('common.size')}：{size || '-'}</span>
                </div>
                <div style={{"display": "flex", "justifyContent": "space-between"}}><span>{getDate(new Date(time))} {getTime(new Date(time))}</span>  <span>{dir == 'default' ? t('common.defaultDirectory') : dir}</span></div>
              </div>
            </div>
          </div>
        ) : (
          // 桌面端布局：传统布局
          <div className="info">
            <h3
              className="file-name"
              style={{
                direction: "rtl",
                paddingBottom: '7px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                margin: 0,
                "height": "22px",
                "fontFamily": "PingFangSC",
                "fontWeight": 400,
                "fontSize": "16px",
                "color": illicitReason ? "#ff4d4f" : "#21242E",
                "letterSpacing": "0",
                textAlign: "left",
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              title={illicitReason || decodeURIComponent(getName(decodeURIComponent(fileName)))}
            >
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {illicitReason || decodeURIComponent(getName(decodeURIComponent(fileName)))}
              </span>
              {illicitReason && (
                <Tooltip title={t('error.contactOnError')}>
                  <QuestionCircleOutlined style={{ fontSize: '14px', color: '#ff4d4f', cursor: 'help', flexShrink: 0 }} />
                </Tooltip>
              )}
            </h3>
            <span
              className="text-info"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                "height": "22px",
                "fontFamily": "PingFangSC",
                "fontWeight": 400,
                "fontSize": "16px",
                "color": "#757D8B",
                "letterSpacing": "0"
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {storageType ? (
                  <>
                    <span style={{ color: storageType === 'TEMPORARY' ? '#ff4d4f' : 'inherit' }}>
                      {getExpireTimeDisplay(storageType, expireTime)}
                    </span>
                    {storageType === 'TEMPORARY' && (
                      <Tooltip title={t('common.temporaryStorageHint')}>
                        <InfoCircleOutlined style={{ fontSize: '14px', color: '#ff4d4f', cursor: 'help' }} />
                      </Tooltip>
                    )}
                    {storageType === 'LONG_TERM' && (
                      <Tooltip title={t('common.clickToSetExpireTime')}>
                        <EditOutlined 
                          style={{ fontSize: '14px', color: '#1890ff', cursor: 'pointer' }} 
                          onClick={() => setExpireTimeModalOpen(true)}
                        />
                      </Tooltip>
                    )}
                  </>
                ) : (
                  `${t('common.format')}：${getFileType(contentType, fileName)}`
                )}
              </span>
              <span>{getDate(new Date(time))} {getTime(new Date(time))}</span>
            </span>
            <span
              className="text-info"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                "height": "22px",
                "fontFamily": "PingFangSC",
                "fontWeight": 400,
                "fontSize": "16px",
                "color": "#757D8B",
                "letterSpacing": "0"
              }}
            >
              <span>{t('common.size')}：{size || '-'}</span>
              <span title={t('common.uploadDirectory')}>{dir == 'default' ? t('common.defaultDirectory') : dir}</span>
            </span>
          </div>
        )}
        <hr
          style={{
            marginTop: '14px',
            marginBottom: '14px',
            "borderColor": "#e6eaf0",
            "borderTopWidth": "0px"
          }}
        />
        <div className="links">
          <div
            className="first-line"
            style={{
              marginBottom: '8px'
            }}
          >
            <Space split={<Divider type="vertical"/>} size={8} style={{"display": "flex", "width": "100%", "justifyContent": "space-between"}}>

              {
                !isImg(url) && (
                  <span
                    className={'link'}
                    onClick={() => {
                      copyToClipboard(url, t('common.linkCopied'))
                    }}
                  >{t('common.link')}</span>
                )
              }
              {
                isImg(url) && (
                  <Dropdown trigger={['click', 'hover']} menu={{items: getLinkItems}}>
                      <span style={{whiteSpace: 'nowrap', color: '#2095f2', textDecoration: 'underline'}} className={'link'}>
                        {t('common.link')}
                      </span>
                  </Dropdown>
                )
              }

              <span
                className={'link'}
                onClick={() => {
                  copyToClipboard(`![${getName(fileName)}](${url})`, t('common.markdownLinkCopied'))
                }}
              >
              MD
            </span>
              {
                isZip(fileName) && (
                  <>
                    <span
                      className={'link'}
                      onClick={() => {
                        const unzipLoadingKey = 'unzipLoadingKey';
                        message.info({
                          content: t('uploadItem.extracting'),
                          key: unzipLoadingKey
                        })
                        const decodedUrl = decodeURIComponent(url);
                        const objectKey = url.substring(decodedUrl.indexOf('autoupload/'));
                        fetch(`/img/unzip-list?objectKey=${objectKey}`).then(res => res.json()).then(res => {
                          message.destroy(unzipLoadingKey);
                          const files = (res.data || []).map((file: {objectKey: string, size: string, url?: string;}) => {
                            const newObjectKey = file.objectKey?.replace(decodeURIComponent(objectKey) + '/', '');
                            return {
                              objectKey: newObjectKey,
                              finalLink: file.url,
                              size: file.size
                            }
                          });
                          Modal.info({
                            title: t('uploadItem.extractContent'),
                            width: '70%',
                            content: (
                              <div
                                className={"unzip-list-ctn"}
                                style={{
                                  marginRight: '34px'
                                }}
                              >
                                <p className={"url"}>
                                  <a href={url} target={'_blank'}>{decodedUrl}</a>
                                </p>
                                <div className="file-list">
                                  <ul
                                    style={{
                                      paddingLeft: '15px',
                                      maxHeight: 'calc(100vh - 300px)',
                                      overflow: 'auto'
                                  }}
                                  >
                                    {
                                      files.map(({objectKey, size, finalLink}: {objectKey: string, size: string, finalLink?: string;}) => {
                                        return (
                                          <li key={objectKey}>
                                            <a href={finalLink || `${url}/${objectKey}`} target={'_blank'}>{finalLink || decodeURIComponent(objectKey)}</a>
                                            &nbsp;
                                            <Tag
                                              color="magenta"
                                              icon={
                                                <CopyOutlined
                                                  style={{
                                                    cursor: 'pointer',
                                                  }}
                                                  onClick={() => {
                                                    copyToClipboard(finalLink || `${url}/${objectKey}`, t('common.linkCopied'));
                                                  }}
                                                />
                                              }
                                            >
                                              {size}
                                            </Tag>
                                          </li>
                                        )
                                      })
                                    }
                                  </ul>
                                </div>
                              </div>
                            )
                          })
                        })
                      }}
                    >
                    {t('uploadItem.extract')}
                  </span>
                  </>
                )
              }
              {
                isImg(fileName) && (
                  <>
                    <Dropdown trigger={['click', 'hover']} menu={{items: photoProcessItems}}>
                      <span style={{whiteSpace: 'nowrap', color: '#2095f2', textDecoration: 'underline'}} className={'link'}>
                        {t('common.photoProcessing')}
                      </span>
                    </Dropdown>
                  </>
                )
              }
              {!isEnglishSite() && (
                <span
                  className={'link'}
                  style={index == 0 ? {
                    color: "red",
                    fontWeight: "bolder",
                    textDecoration: "underline"
                  } : {}}
                  onClick={() => {
                    handleCopyOriginUrl(rawUrl || url.replace(/(([\dcdn]+.))?z.wiki/, 'z.wiki'))
                  }}
                >
                  {t('common.originalImage')}
                </span>
              )}
            </Space>
          </div>
          <div
            className="second-line"
          >
            <Space split={<Divider type="vertical"/>} size={8} style={{"display": "flex", "width": "100%", "justifyContent": "space-between"}}>
            <span
              className={'link'}
              onClick={() => {
                window.open(url, '_blank');
              }}
            >
              {t('common.access')}
            </span>
              <span
                className={'link'}
                onClick={() => {
                  console.info(t('common.qrCode'))
                  Modal.info({
                    title: t('common.qrCode'),
                    content: (
                      <div
                        className={"qr-code-ctn"}
                        style={{
                          marginRight: '34px'
                        }}
                      >
                        <p className={"url"}>
                          <a href={url} target={'_blank'}>{decodeURIComponent(url)}</a>
                        </p>
                        <div className="qr-code">
                          <QRCode
                            size={256}
                            style={{height: "auto", maxWidth: "100%", width: "100%"}}
                            value={url}
                            viewBox={`0 0 256 256`}
                          />
                        </div>
                      </div>
                    )
                  })
                }}
              >
                {t('common.qrCode')}
              </span>
              <span
                className={'link'}
                onClick={() => {
                  setShortUrlModalOpen(true);
                }}
              >
              {t('common.shortUrl')}
            </span>
              <Dropdown trigger={['click', 'hover']} menu={{items: moreItems}}>
                <span style={{whiteSpace: 'nowrap'}} className={'link'}>
                    {t('common.more')}
                </span>
              </Dropdown>
              {/*  <span*/}
              {/*    style={{display: 'none'}}*/}
              {/*    className={'link dlt-link'}*/}
              {/*    onClick={() => {*/}
              {/*    }}*/}
              {/*  >*/}
              {/*  删除*/}
              {/*</span>*/}
            </Space>
          </div>
        </div>
      </div>
      <ShortUrlModal
        open={shortUrlModalOpen}
        onCancel={() => setShortUrlModalOpen(false)}
        url={url}
      />
      <ExpireTimeModal
        open={expireTimeModalOpen}
        onCancel={() => setExpireTimeModalOpen(false)}
        recordId={id}
        currentExpireTime={expireTime ? new Date(expireTime) : null}
        onSuccess={() => onRefresh?.()}
        isVip={isVip}
      />
      <EncryptedLinkModal
        open={encryptedLinkModalOpen}
        onCancel={() => setEncryptedLinkModalOpen(false)}
        uploadId={id}
        fileName={fileName}
      />
    </div>
  );
}

export default UploadItem
